<script lang="ts" setup>
import type {IButton} from "~/shared/interfaces/ui/buttons";

withDefaults(defineProps<IButton>(), {
  type: 'primary',
  size: 'm',
  fontWeight: 500,
  textTransform: 'none',
  direction: 'row',
  gap: '8px',
})
</script>

<template>
  <button
    v-if="!href"
    class="button"
    :class="[
      `button_${type}`,
      `button_${size}`,
      subType && `button_${type}_${subType}`,
      isIconButtonRounded && `button_icon_rounded`,
      isInverse && `button_${type}${subType ? `_${subType}` : ''}_inverse`,
      isAdaptive && `button_${type}_adaptive`,
      isDisabled && 'disabled',
      isActive && 'active',
    ]"
    :disabled="isDisabled"
  >
    <component
      v-if="icon"
      :is="icon"
    />
    <span
      class="button__text"
      v-if="text"
    >
      {{ text }}
    </span>
    <span
      class="button__sub-text"
      v-if="subText"
    >
      {{ subText }}
    </span>
  </button>
  <NuxtLink
    v-else
    class="button"
    :class="[
      `button_${type}`,
      `button_${size}`,
      subType && `button_${type}_${subType}`,
      isIconButtonRounded && `button_icon_rounded`,
      isInverse && `button_${type}${subType ? `_${subType}` : ''}_inverse`,
      isAdaptive && `button_${type}_adaptive`,
      isDisabled && 'disabled',
      isActive && 'active',
    ]"
    :to="href"
  >
    <component
      v-if="icon"
      :is="icon"
    />
    <span
      class="button__text"
      v-if="text"
    >
      {{ text }}
    </span>
    <span
      class="button__sub-text"
      v-if="subText"
    >
      {{ subText }}
    </span>
  </NuxtLink>
</template>

<style lang="scss" scoped>
.button {
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: v-bind(direction);
  gap: v-bind(gap);
  text-transform: v-bind(textTransform);
  @include font(16px, 20px, v-bind(fontWeight));
  transition: color .3s, background-color .3s, opacity 3s, border-color .3s;
  white-space: nowrap;
  flex-shrink: 0;
  width: fit-content;

  &_xs, &_s {
    @include font(14px, 20px, v-bind(fontWeight));
  }
  &_xxs {
    height: 40px;
    min-width: 40px;
    display: flex;
    align-items: center;
  }

  &_xs {
    height: 36px;
    padding: 0 12px;
    min-width: 72px;
  }

  &_s {
    height: 40px;
    padding: 0 16px;
    min-width: 80px;
  }

  &_m {
    height: 48px;
    padding: 0 20px;
    min-width: 96px;
  }

  &_l {
    height: 56px;
    padding: 0 24px;
    min-width: 112px;
  }

  &_primary {
    background-color: $control-bg_primary;
    color: $control-content_white;
    @include btn-svg-color($control-content_white);

    &_adaptive {
      @include md-breakpoint {
        width: 100% !important;
      }
    }

    &:hover:not(:active, .active, .disabled) {
      @include mf-breakpoint {
        background-color: $control-bg_primary-hover;
      }
    }

    &_inverse {
      background-color: $control-bg_white;
      color: $control-content_primary;
      @include btn-svg-color($control-content_primary);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $control-bg_white;
          color: $control-content_orange;
          @include btn-svg-color($control-content_orange);
        }
      }
    }
  }

  &_secondary {
    background-color: $control-bg_secondary;
    color: $control-content_orange;

    &:hover:not(:active, .active, .disabled) {
      @include mf-breakpoint {
        background-color: $control-bg_secondary-hover;
      }
    }

    @include btn-svg-color($control-content_orange);
  }

  &_neutral {
    background-color: $control-bg_neutral;
    color: $control-content_primary;
    @include btn-svg-color($control-content_primary);

    &:hover:not(:active, .active, .disabled) {
      @include mf-breakpoint {
        background-color: $control-bg_neutral-hover;
      }
    }

    &.active {
      background-color: $control-bg_primary;
      color: $control-content_white;
      @include btn-svg-color($content_white)
    }
  }

  &_outlined {
    border: 1px solid $control-border_grey;

    &.button {
      &_xs {
        padding: 0 11px;
      }

      &_s {
        padding: 0 15px;

      }

      &_m {
        padding: 0 19px;

      }

      &_l {
        padding: 0 23px;
      }
    }

    &:hover:not(:active, .active, .disabled) {
      @include mf-breakpoint {
        border: 2px solid $control-border_black;
      }

      &.button {
        &_xs {
          padding: 0 10px;
        }

        &_s {
          padding: 0 14px;

        }

        &_m {
          padding: 0 18px;

        }

        &_l {
          padding: 0 22px;
        }
      }
    }

    @include btn-svg-color($control-content_primary);

    &.disabled {
      color: $control-content_disabled;
      cursor: auto;
      pointer-events: none;

      @include btn-svg-color($control-content_disabled);
    }
  }

  &_black {
    background-color: $control-bg_black;
    color: $control-content_white;

    &:hover:not(:active, .active, .disabled) {
      @include mf-breakpoint {
        background-color: $control-bg_black-hover;
      }
    }

    @include btn-svg-color($control-content_white);
  }

  &_ghost {
    height: auto;
    padding: 0;
    min-width: auto;

    &:hover:not(:active, .active, .disabled){
      @include mf-breakpoint {
        color: $control-content_orange;
        @include btn-svg-color($control-content_orange);
      }
    }

    &.button {
      &_xs, &_s {
        gap: 4px;
        @include md-breakpoint {
          padding: 2px 0;
        }
      }

      &_m, &_l {
        gap: 8px;
        @include md-breakpoint {
          padding: 4px 0;
        }
      }
    }

    &_primary {
      color: $control-content_orange;
      @include btn-svg-color($control-content_orange);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          color: $control-content_orange-hover;
          @include btn-svg-color($control-content_orange-hover);
        }
      }
    }

    &_neutral {
      color: $control-content_primary;
      @include btn-svg-color($control-content_primary);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          color: $control-content_orange-hover;
          @include btn-svg-color($control-content_orange-hover);
        }
      }
    }

    &_negative {
      color: $control-content_negative;
      @include btn-svg-color($control-content_negative);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          color: $control-content_negative-hover;
          @include btn-svg-color($control-content_negative-hover);
        }
      }
    }

    &.disabled {
      color: $control-content_disabled;
      @include btn-svg-color($control-content_disabled);
      cursor: auto;
      pointer-events: none;
    }
  }

  &_two-rows {
    color: $control-content_white;
    flex-direction: column;
    gap: 2px;

    &_primary {
      background-color: $control-bg_primary;

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $control-bg_primary-hover;
        }
      }
    }

    &_black {
      background-color: $control-bg_black;

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $control-bg_black-hover;
        }
      }
    }

    &.button {
      &_xs, &_s, &_m, &_l {
        height: 56px;
        padding: 0 24px;
      }
    }

    .button {
      &__text {
        @include font(16px, 20px, 500);
      }

      &__sub-text {
        @include font(12px, 16px, 400);
      }
    }
  }

  &_icon {
    padding: 0;
    min-width: auto;

    &.button {
      &_xs {
        width: 36px;
      }

      &_s {
        width: 40px;
      }

      &_m {
        width: 48px;
      }

      &_l {
        width: 56px;
      }
    }

    &_primary {
      background-color: $control-bg_primary;
      @include btn-svg-color($control-content_white);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $control-bg_primary-hover;
        }
      }

      &_inverse {
        background-color: $control-bg_white;
        @include btn-svg-color($control-content_primary);

        &:hover:not(:active, .active, .disabled) {
          @include mf-breakpoint {
            background-color: $control-bg_white;
            @include btn-svg-color($control-content_orange);
          }
        }

        &.disabled {
          background-color: $control-bg_inverse-disabled !important;
        }
      }
    }

    &_secondary {
      background-color: $control-bg_secondary;
      @include btn-svg-color($control-content_orange);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $control-bg_secondary-hover;
        }
      }
    }

    &_neutral {
      background-color: $control-bg_neutral;
      @include btn-svg-color($control-content_primary);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $control-bg_neutral-hover;
        }
      }
    }

    &_overlay {
      background-color: $control-bg_white;
      box-shadow: 0 4px 20px 0 #00000014;
      @include btn-svg-color($control-content_primary);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          @include btn-svg-color($control-content_orange);
        }
      }
    }

    &_footer {
      background-color: $footer_control-bg-grey;
      @include btn-svg-color($control-content_white);

      &:hover:not(:active, .active, .disabled) {
        @include mf-breakpoint {
          background-color: $footer_control-bg-grey-hover;
        }
      }

      &.disabled {
        pointer-events: none;
        cursor: auto;
        background-color: $footer_control-bg-grey;
        @include btn-svg-color($control-content_disabled);
      }
    }

    &_rounded {
      border-radius: 50%;
    }
  }

  &_primary, &_secondary, &_neutral, &_black, &_two-rows, &_icon {
    &.disabled {
      background-color: $control-bg_disabled;
      color: $control-content_disabled;
      cursor: auto;
      pointer-events: none;
      @include btn-svg-color($control-content_disabled);
    }
  }
}
</style>
